import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";
import { money, thisMonth } from "../../lib/format";

const TAGS = ["Health", "Social", "Travel", "Repairs", "Shopping", "Fees", "Other"];

export default function UnplannedManager({ open, onOpenChange, month, currency, onChanged, readOnly }) {
  const [items, setItems] = useState([]);
  const [item, setItem] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Other");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(`${month}-01`);

  const load = useCallback(async () => {
    const { data } = await api.get(`/unplanned?month=${month}`);
    setItems(data);
  }, [month]);

  useEffect(() => { if (open) { load(); setDate(month === thisMonth() ? new Date().toISOString().slice(0, 10) : `${month}-01`); } }, [open, load, month]);

  const add = async () => {
    if (readOnly) return toast.error("View-only account — editing is disabled.");
    if (!item || !amount) return toast.error("Enter item and amount");
    await api.post("/unplanned", { item, amount: Number(amount), category, reason, date, month });
    setItem(""); setAmount(""); setReason("");
    toast.success("Expense logged");
    load(); onChanged();
  };

  const del = async (id) => { if (readOnly) return toast.error("View-only account — editing is disabled."); await api.delete(`/unplanned/${id}`); load(); onChanged(); };

  const total = items.reduce((s, i) => s + Number(i.amount), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader><DialogTitle className="font-display">Unplanned Budget Wallet</DialogTitle><DialogDescription>Log unexpected expenses. Totals feed into your My Wallet free-money calculation.</DialogDescription></DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-end bg-slate-50 p-3 rounded-xl">
          <Input data-testid="unplanned-item-input" placeholder="Item" value={item} onChange={(e) => setItem(e.target.value)} />
          <Input data-testid="unplanned-amount-input" type="number" placeholder="Amount" className="font-mono-nums" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="unplanned-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="unplanned-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Input data-testid="unplanned-reason-input" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button data-testid="unplanned-add-button" onClick={add} disabled={readOnly} className="bg-amber-600 hover:bg-amber-700 text-white"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-slate-500">{items.length} expense(s)</span>
          <span className="font-mono-nums font-semibold text-amber-700">Total: {money(total, currency)}</span>
        </div>

        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} data-testid={`unplanned-row-${i.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
              <div className="flex-1">
                <p className="font-medium text-slate-800 text-sm">{i.item}</p>
                <p className="text-xs text-slate-400">{i.category} · {i.date}{i.reason ? ` · ${i.reason}` : ""}</p>
              </div>
              <span className="font-mono-nums font-semibold text-slate-800 text-sm">{money(i.amount, currency)}</span>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(i.id)} data-testid={`unplanned-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-sm text-slate-400 py-6">No unplanned expenses logged this month.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
